import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, FileSignature, Save, ShieldCheck, Upload } from "lucide-react";
import { api, ApiError } from "../../api";
import { Button, Card, Field, PageHeader } from "../../ui";

/**
 * White-glove insurance upload for staff (PM/admin). Lists the
 * target owner's existing policies in the principal's managed
 * buildings, then offers an upload form that POSTs to
 * /api/management/users/:userId/liability-policies. The recorded
 * policy is attributed to the owner; the audit trail records the
 * staff actor.
 *
 * The UI deliberately mirrors the owner-side /my/insurance flow
 * field-for-field so a PM bringing a paper certificate to their
 * desk has the same form they'd walk an owner through over the
 * phone.
 */

type UserDetail = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  ownerships: Array<{
    id: string;
    unitId: string;
    unitNumber: string;
    buildingId: string;
    buildingName: string;
    ownershipType: string;
  }>;
};

type Policy = {
  id: string;
  unitId: string;
  carrierName: string;
  policyNumber: string;
  perIncidentLimitCents: string;
  effectiveDate: string;
  expirationDate: string;
  validationStatus: string;
};

export function AdminUserInsurance() {
  const { userId = "" } = useParams<{ userId: string }>();
  const nav = useNavigate();
  const [target, setTarget] = useState<UserDetail | null>(null);
  const [policies, setPolicies] = useState<Policy[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    try {
      const [u, p] = await Promise.all([
        api.get<{ data: UserDetail }>(`/api/admin/users/${userId}`),
        api.get<{ data: Policy[] }>(
          `/api/management/users/${userId}/liability-policies`,
        ),
      ]);
      setTarget(u.data);
      setPolicies(p.data);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.status === 403
            ? "You don't have access to this owner's insurance."
            : `Load failed (${err.status})`
          : String(err),
      );
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const subtitle = target
    ? `${target.firstName} ${target.lastName} · ${target.email}`
    : undefined;

  return (
    <div className="max-w-3xl">
      <button
        onClick={() => nav(-1)}
        className="text-ink-500 hover:text-ink-800 text-sm inline-flex items-center gap-1 mb-2"
      >
        <ArrowLeft size={14} />
        Back
      </button>
      <PageHeader
        title="White-glove insurance upload"
        subtitle={subtitle}
        eyebrow="Admin"
      />

      {error && (
        <Card>
          <div className="alert-error">{error}</div>
        </Card>
      )}

      <Card>
        <h2 className="text-ink-800 m-0 inline-flex items-center gap-2 mb-2">
          <ShieldCheck size={18} /> Policies on file
        </h2>
        <p className="text-[12.5px] text-ink-500 mb-3">
          Active and historical liability policies on units in your buildings.
          Owner-uploaded policies and white-glove uploads share the same list.
        </p>
        {policies === null ? (
          <p className="text-ink-500 text-sm m-0">Loading…</p>
        ) : policies.length === 0 ? (
          <p className="text-ink-500 text-sm m-0">No policies on file yet.</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Unit</th>
                  <th>Carrier</th>
                  <th>Policy #</th>
                  <th>Limit</th>
                  <th>Effective</th>
                  <th>Expires</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {policies.map((p) => {
                  const ownership = target?.ownerships.find(
                    (o) => o.unitId === p.unitId,
                  );
                  return (
                    <tr key={p.id}>
                      <td>{ownership?.unitNumber ?? "—"}</td>
                      <td>{p.carrierName}</td>
                      <td>
                        <code className="text-xs">{p.policyNumber}</code>
                      </td>
                      <td className="tabular-nums">
                        ${" "}
                        {(
                          Number(p.perIncidentLimitCents) / 100
                        ).toLocaleString("en-US")}
                      </td>
                      <td className="text-xs text-ink-600">
                        {p.effectiveDate}
                      </td>
                      <td className="text-xs text-ink-600">
                        {p.expirationDate}
                      </td>
                      <td>
                        <span
                          className="badge"
                          data-status={
                            p.validationStatus === "passed"
                              ? "approved"
                              : p.validationStatus === "failed"
                                ? "rejected"
                                : "review"
                          }
                        >
                          {p.validationStatus}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {target && target.ownerships.length > 0 && (
        <UploadForm target={target} onSaved={reload} />
      )}

      {target && target.ownerships.length === 0 && (
        <Card>
          <p className="text-[12.5px] text-ink-600 m-0">
            This user doesn't currently own any units in your managed buildings.
            Assign a unit on the{" "}
            <Link
              to={`/admin/users?id=${userId}`}
              className="text-brand-700 hover:text-brand-800"
            >
              user edit page
            </Link>{" "}
            first.
          </p>
        </Card>
      )}
    </div>
  );
}

function UploadForm({
  target,
  onSaved,
}: {
  target: UserDetail;
  onSaved: () => void | Promise<void>;
}) {
  const [unitId, setUnitId] = useState(target.ownerships[0]?.unitId ?? "");
  const [carrierName, setCarrierName] = useState("");
  const [policyNumber, setPolicyNumber] = useState("");
  const [limitDollars, setLimitDollars] = useState("500000");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const limitCents = useMemo(() => {
    const n = Number(limitDollars);
    if (!Number.isFinite(n) || n <= 0) return null;
    return BigInt(Math.round(n * 100));
  }, [limitDollars]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setSaved(false);
    setError(null);
    try {
      if (!unitId) throw new Error("Pick a unit.");
      if (limitCents === null) throw new Error("Limit must be a positive number.");
      const fd = new FormData();
      fd.append("unitId", unitId);
      fd.append("carrierName", carrierName);
      fd.append("policyNumber", policyNumber);
      fd.append("perIncidentLimitCents", limitCents.toString());
      fd.append("effectiveDate", effectiveDate);
      fd.append("expirationDate", expirationDate);
      if (file) fd.append("file", file);
      const res = await fetch(
        `/api/management/users/${target.id}/liability-policies`,
        { method: "POST", credentials: "include", body: fd },
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
        };
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      setSaved(true);
      // Clear the inputs that change per-policy; keep the unit
      // selection for back-to-back uploads of multiple owners on
      // the same unit (rare, but it happens with joint owners).
      setCarrierName("");
      setPolicyNumber("");
      setEffectiveDate("");
      setExpirationDate("");
      setFile(null);
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <h2 className="text-ink-800 m-0 inline-flex items-center gap-2 mb-2">
        <FileSignature size={18} /> Record a new policy
      </h2>
      <p className="text-[12.5px] text-ink-500 mb-3">
        Same fields as the owner-side upload. The certificate file is
        optional; you can attach it later by re-uploading. Limit must be at
        least $500,000 per the Construction Rules.
      </p>
      <form onSubmit={submit}>
        <Field label="Unit *">
          <select
            className="select"
            value={unitId}
            onChange={(e) => setUnitId(e.target.value)}
            required
          >
            {target.ownerships.map((o) => (
              <option key={o.id} value={o.unitId}>
                Unit {o.unitNumber} · {o.buildingName} · {o.ownershipType}
              </option>
            ))}
          </select>
        </Field>
        <div className="field-row">
          <Field label="Carrier name *">
            <input
              className="input"
              value={carrierName}
              onChange={(e) => setCarrierName(e.target.value)}
              required
            />
          </Field>
          <Field label="Policy number *">
            <input
              className="input"
              value={policyNumber}
              onChange={(e) => setPolicyNumber(e.target.value)}
              required
            />
          </Field>
        </div>
        <Field
          label="Per-incident limit (USD) *"
          hint="Minimum $500,000 per the Construction Rules."
        >
          <input
            className="input"
            type="number"
            min={500000}
            step={1000}
            value={limitDollars}
            onChange={(e) => setLimitDollars(e.target.value)}
            required
          />
        </Field>
        <div className="field-row">
          <Field label="Effective date *">
            <input
              className="input"
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
              required
            />
          </Field>
          <Field label="Expiration date *">
            <input
              className="input"
              type="date"
              value={expirationDate}
              onChange={(e) => setExpirationDate(e.target.value)}
              required
            />
          </Field>
        </div>
        <Field
          label="Certificate (optional)"
          hint="PDF or image. Goes through the same virus scan as owner uploads."
        >
          <input
            type="file"
            accept="application/pdf,image/*"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </Field>
        {saved && <div className="alert-success">Policy recorded.</div>}
        {error && <div className="alert-error">{error}</div>}
        <Button variant="primary" type="submit" loading={busy}>
          <Upload size={14} /> {busy ? "Saving…" : "Record policy"}
        </Button>
        <p className="text-[10.5px] text-ink-400 mt-3 leading-snug">
          <Save size={11} className="inline-block mr-1" />
          Audit logged as a white-glove action. The owner sees the policy on
          their /my/insurance page and uses it as proof of coverage at project
          submit time, just as if they'd uploaded it themselves.
        </p>
      </form>
    </Card>
  );
}
